import { Icon, Intent, Spinner, Tooltip } from "@blueprintjs/core"
import { useQueryClient } from "@tanstack/react-query"
import { AxiosError } from "axios"
import React, { useRef, useState } from "react"
import { toaster } from "../../../utils/createToaster"
import useUploadDatasetCSVMutation from "../hooks/useUploadDatasetCSVMutation"
import { UploadButton, UploadContainer } from "./DatasetUploadButton.styled"

const DatasetUploadButton = () => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const queryClient = useQueryClient()

  const uploadMutation = useUploadDatasetCSVMutation({
    onSuccess: data => {
      toaster.show({
        message: "Upload successful! Processing datasets...",
        intent: Intent.SUCCESS,
      })
      setIsUploading(false)
      queryClient.invalidateQueries({ queryKey: ["trading_datasets"] })
    },
    onError: (error: AxiosError<{ error: string }>) => {
      const errorMessage =
        error.response?.data?.error ||
        "Failed to upload CSV. Please check the file format and try again."
      toaster.show({
        message: errorMessage,
        intent: Intent.DANGER,
      })
      setIsUploading(false)
    },
  })

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.name.endsWith(".csv")) {
      toaster.show({
        message: "Please select a CSV file",
        intent: Intent.DANGER,
      })
      return
    }

    // Validate file size (limit to 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toaster.show({
        message: "File size must be less than 10MB",
        intent: Intent.DANGER,
      })
      return
    }

    setIsUploading(true)
    uploadMutation.mutate(file)

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const handleButtonClick = () => {
    fileInputRef.current?.click()
  }

  const tooltipContent = (
    <div style={{ maxWidth: "350px" }}>
      <strong>Expected CSV Format:</strong>
      <br />
      <br />• First column: <code>datetime</code>
      <br />• Other columns: <code>CODE Scient</code> or <code>CODE Scient|Name</code>
      <br />
      <br />
      <strong>Examples:</strong>
      <br />• <code>US_CPI_YOY Scient</code>
      <br />• <code>US_CPI_YOY Scient|US CPI Year over Year</code>
      <br />
      <br />
      <strong>Requirements:</strong>
      <br />• Code must end with " Scient"
      <br />• Code must be less than 60 characters
      <br />• Max file size: 10MB
      <br />• Empty cells are skipped
    </div>
  )

  return (
    <UploadContainer flexDirection="row" alignItems="center" gap="5px">
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        onChange={handleFileChange}
        style={{ display: "none" }}
      />
      <UploadButton
        icon={isUploading ? <Spinner size={16} /> : "upload"}
        text={isUploading ? "Uploading..." : "Upload Dataset CSV"}
        onClick={handleButtonClick}
        disabled={isUploading}
        intent={Intent.PRIMARY}
        outlined
      />
      <Tooltip content={tooltipContent} position="right">
        <Icon icon="info-sign" size={18} />
      </Tooltip>
    </UploadContainer>
  )
}

export default DatasetUploadButton
